import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { usePermissions } from "./usePermissions";
import { CollinsUser } from "../types/user";
import { VenueGroupConfig } from "../types/VenueGroup";
import type { WootricDisclaimer } from "../types/wootric";
import { getDeviceOS } from "../utils/getDeviceOS";
import { getEnvironment, isBrowser } from "../utils/environment";
import { loadWootricSdk } from "../utils/wootricSdk";

const WOOTRIC_PRODUCT_NAME = "Access Collins";
const WOOTRIC_USER_ROLE = "user";
const WOOTRIC_THEME_NAME = "light";

/**
 * Required GDPR obligation. Do not remove without legal sign-off.
 */
const WOOTRIC_DISCLAIMER: WootricDisclaimer = {
  text: "Learn how we handle your feedback",
  link: "https://www.theaccessgroup.com/en-gb/privacy-notice/",
  link_word: "here"
};

/**
 * Client identifiers reported to Wootric so responses from Collins 2.0 desktop
 * web can be segmented from the native mobile app. The legacy `collins-ui`
 * client sets no `productVersion`, so an absent value infers the legacy UI.
 */
const WOOTRIC_PRODUCT_VERSION_WEB = "collins-web-2.0";
const WOOTRIC_PRODUCT_VERSION_NATIVE = "collins-mobile-native";

/**
 * Routes on which the Wootric NPS widget must be suppressed, mirroring the
 * legacy `hideWootric` flag on the diary/grid/run-sheet/floorplan states in
 * `collins-ui` (`src/js/configs/routeConfig.js`).
 */
const SUPPRESSED_DIARY_TABS = ["diary", "run-sheet", "floorplan"];

interface UseWootricProps {
  user?: CollinsUser;
  venueGroupConfig?: VenueGroupConfig | null;
  isAuthenticated: boolean;
}

const isWootricSuppressedRoute = (pathname: string): boolean => {
  if (pathname === "/diary" || pathname.startsWith("/diary/")) {
    return true;
  }

  const siteTabMatch = /^\/sites\/[^/]+\/([^/?]+)\/?$/.exec(pathname);
  if (siteTabMatch) {
    return SUPPRESSED_DIARY_TABS.includes(siteTabMatch[1]);
  }

  return false;
};

/**
 * Reads a `true`/`false` cookie by name, matching the legacy `collins-ui`
 * debugging switches.
 */
const readBooleanCookie = (cookieName: string): boolean => {
  const parts = `; ${document.cookie}`.split(`; ${cookieName}=`);

  return parts.length === 2 && parts.pop()?.split(";").shift() === "true";
};

const clearWootricSimulationFlags = (): void => {
  delete window.wootric_no_surveyed_cookie;
  delete window.wootric_survey_immediately;
};

/**
 * Loads the Wootric NPS survey widget for external (non-internal) users,
 * replicating the legacy `collins-ui` integration in the React app.
 *
 * The widget only loads when a `WOOTRIC_ACCOUNT_TOKEN` is configured for the
 * active environment, once the user is authenticated and their user and
 * venue group config are available, and never for internal users. It is
 * stopped while the operator is on a suppressed diary route.
 */
export const useWootric = ({
  user,
  venueGroupConfig,
  isAuthenticated
}: UseWootricProps): void => {
  const location = useLocation();
  const permissions = usePermissions();
  const isRunningRef = useRef(false);

  const userIsInternal = permissions.roles.derived.hasRoles
    ? permissions.roles.derived.userIsInternal
    : permissions.capabilities.derived.userIsInternal;

  const stopWootric = (): void => {
    if (isRunningRef.current) {
      window.WootricSurvey?.stop();
      clearWootricSimulationFlags();
      isRunningRef.current = false;
    }
  };

  useEffect(() => {
    const startWootric = async () => {
      if (
        !isAuthenticated ||
        !user ||
        !venueGroupConfig ||
        userIsInternal ||
        isWootricSuppressedRoute(location.pathname)
      ) {
        stopWootric();
        return;
      }

      if (isRunningRef.current) {
        return;
      }

      try {
        const environment = await getEnvironment();
        const accountToken = environment?.WOOTRIC_ACCOUNT_TOKEN;

        if (!accountToken) {
          return;
        }

        const createdAt = user.created_date
          ? Math.floor(new Date(user.created_date).getTime() / 1000)
          : Math.floor(Date.now() / 1000);

        window.wootricSettings = {
          account_token: accountToken,
          email: user.email ?? "",
          created_at: createdAt,
          modal_theme: WOOTRIC_THEME_NAME,
          properties: {
            product: WOOTRIC_PRODUCT_NAME,
            customerName: venueGroupConfig.title ?? "",
            userRole: WOOTRIC_USER_ROLE,
            productReportingCode: environment?.ACCESS_PRODUCT_CODE ?? "",
            deviceOS: getDeviceOS(),
            productVersion: isBrowser()
              ? WOOTRIC_PRODUCT_VERSION_WEB
              : WOOTRIC_PRODUCT_VERSION_NATIVE
          },
          disclaimer: WOOTRIC_DISCLAIMER
        };

        if (readBooleanCookie("wootrics_simulateDisplay")) {
          window.wootric_no_surveyed_cookie = true;
          window.wootric_survey_immediately = true;
        } else {
          clearWootricSimulationFlags();
        }

        if (readBooleanCookie("wootrics_showDebugInfo")) {
          console.info("Wootric settings", window.wootricSettings);
        }

        await loadWootricSdk();

        if (!window.WootricSurvey) {
          console.error("Wootric SDK loaded but WootricSurvey was not defined");
          return;
        }

        window.WootricSurvey.run();
        isRunningRef.current = true;
      } catch (error) {
        console.error("Failed to load Wootric", error);
      }
    };

    startWootric();
  }, [
    isAuthenticated,
    user,
    venueGroupConfig,
    userIsInternal,
    location.pathname
  ]);

  useEffect(() => {
    return () => {
      stopWootric();
    };
  }, []);
};
